import React, { useState } from 'react';

export const SUGGEST_INSTRUCTION_MAX = 1000;

/**
 * The AI revision controls for the selected letter paragraph.
 *
 * "Suggest changes" (TDI-404): the clinician writes what they want changed
 * and the AI returns a revised paragraph. "Polish my edits" (TDI-407): after
 * a manual edit, the AI tidies the clinician's wording. Both come back as a
 * PROPOSAL shown here beside the current paragraph; nothing changes in the
 * letter until the clinician chooses "Use this version", and "Keep the
 * current text" discards it. The clinician reads the proposal before it can
 * become part of the letter, which is what keeps the AI to drafting.
 */
const PROPOSAL_TITLES = {
  suggest_changes: 'Suggested revision',
  polish: 'Tidied version',
  evidence_change: 'Redrafted from the adjusted evidence',
};

export function ParagraphRevisionPanel({
  canPolish,
  evidenceAdjusted,
  busyMode,
  proposal,
  onSuggest,
  onPolish,
  onRegenerateFromEvidence,
  onAcceptProposal,
  onDiscardProposal,
}) {
  const [instruction, setInstruction] = useState('');
  const busy = Boolean(busyMode);
  const trimmed = instruction.trim();

  const submitSuggestion = (event) => {
    event.preventDefault();
    if (!trimmed || busy) return;
    onSuggest(trimmed);
  };

  return (
    <div className="p-4 pb-0" data-testid="paragraph-revision-panel">
      {proposal ? (
        <div
          className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-left"
          role="region"
          aria-label="Proposed revision"
          data-testid="revision-proposal"
        >
          <p className="text-sm font-semibold text-gray-900">
            {PROPOSAL_TITLES[proposal.mode] || 'Suggested revision'}
          </p>
          {proposal.mode === 'suggest_changes' && proposal.instruction && (
            <p className="mt-1 text-xs text-gray-600">
              From your instruction: <span className="italic">&quot;{proposal.instruction}&quot;</span>
            </p>
          )}
          {proposal.unchanged ? (
            <p className="mt-2 text-sm text-gray-800">
              The AI found nothing to change. The paragraph stays as it is.
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-800 whitespace-pre-wrap" data-testid="revision-proposal-text">
              {proposal.text}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-600">
            Read it carefully: AI can make mistakes. Using it replaces the paragraph and it will need
            approving again.
          </p>
          <div className="mt-3 flex gap-2">
            {!proposal.unchanged && (
              <button
                type="button"
                onClick={onAcceptProposal}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary/90"
              >
                Use this version
              </button>
            )}
            <button
              type="button"
              onClick={onDiscardProposal}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-primary/40 text-primary hover:bg-primary/10"
            >
              Keep the current text
            </button>
          </div>
        </div>
      ) : (
        <>
          {evidenceAdjusted && (
            <div className="mb-3 text-left" data-testid="evidence-regenerate">
              <button
                type="button"
                onClick={onRegenerateFromEvidence}
                disabled={busy}
                className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busyMode === 'evidence_change' ? 'Redrafting…' : 'Regenerate from the adjusted evidence'}
              </button>
              <p className="mt-1 text-xs text-gray-600">
                Evidence adjusted below: {evidenceAdjusted}. The redraft comes back for you to read
                before it replaces the paragraph.
              </p>
            </div>
          )}
          <form onSubmit={submitSuggestion} className="text-left">
            <label htmlFor="suggest-changes-instruction" className="block text-sm font-semibold text-gray-900">
              Suggest changes
            </label>
            <p className="mt-1 text-xs text-gray-600">
              Say what you want changed in this paragraph, for example &quot;shorter and mention the
              school&apos;s view&quot;. The AI uses only the recorded information below.
            </p>
            <textarea
              id="suggest-changes-instruction"
              value={instruction}
              onChange={(event) => setInstruction(event.target.value.slice(0, SUGGEST_INSTRUCTION_MAX))}
              rows={3}
              disabled={busy}
              className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm text-gray-900 focus:border-primary focus:ring-primary disabled:bg-gray-100"
            />
            <button
              type="submit"
              disabled={!trimmed || busy}
              className="mt-2 w-full px-4 py-2 rounded-lg text-sm font-medium border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busyMode === 'suggest_changes' ? 'Revising…' : 'Suggest changes'}
            </button>
          </form>
          {canPolish && (
            <div className="mt-3 text-left">
              <button
                type="button"
                onClick={onPolish}
                disabled={busy}
                className="w-full px-4 py-2 rounded-lg text-sm font-medium border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busyMode === 'polish' ? 'Tidying…' : 'Polish my edits'}
              </button>
              <p className="mt-1 text-xs text-gray-600">
                Tidies the wording of your edit without changing what it says. You choose which
                version to keep.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ParagraphRevisionPanel;
